// biome-ignore-all lint/suspicious/noThenProperty: `then` is the rule DSL's action clause (blueprint §7.3.1).
/**
 * Rule DSL (blueprint §7.3.1) as TypeBox schemas: the API validates custom rules with them and
 * `@brecx/core` rules evaluate them. Metric refs point at economics so rules follow profit.
 */
import { type Static, type TLiteral, type TSchema, type TUnion, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

export const RULE_SCOPES = ["campaign", "ad_group", "keyword", "search_term", "product", "account"] as const;
export const RULE_ACTIONS = [
  "pause",
  "enable",
  "set_budget",
  "adjust_budget_pct",
  "set_target_roas",
  "adjust_target_pct",
  "set_cpc",
  "adjust_cpc_pct",
  "add_negative",
  "harvest_exact",
  "add_keyword",
  "notify",
] as const;
export const AUTONOMY_LEVELS = ["off", "suggest", "auto_undo", "auto"] as const;
export const COMPARATORS = ["=", "!=", ">", ">=", "<", "<="] as const;

/** Metrics every entity exposes over the rule window (money in account currency units). */
export const RULE_METRICS = [
  "clicks",
  "impressions",
  "ctr",
  "cost",
  "conversions",
  "value",
  "cpa",
  "conv_rate",
  "conv_rate_lower",
  "roas",
  "poas",
  "lost_is_budget",
  "on_hand",
  "enabled_ad_groups",
  "pacing_pct",
  "tier_target_gap_pct",
  "bid_gap_pct",
  "is_competitor",
  "blocked_by_negative",
  "paused_days",
] as const;

/** Economics and workspace references usable as values. */
export const RULE_REFS = [
  "product.max_cpa",
  "product.break_even_cpa",
  "product.target_roas",
  "product.conv_rate",
  "product.ctr",
  "tier.target_roas",
  "campaign.target_roas",
  "workspace.target_poas",
  "workspace.min_conversions",
  "workspace.spend_alert_pct",
] as const;

function lit<const T extends readonly string[]>(xs: T): TUnion<TLiteral<T[number]>[]> {
  return Type.Union(xs.map((x) => Type.Literal(x))) as TUnion<TLiteral<T[number]>[]>;
}

export const RuleScopeSchema = lit(RULE_SCOPES);
export const RuleActionSchema = lit(RULE_ACTIONS);
export const AutonomySchema = lit(AUTONOMY_LEVELS);

export const RefValueSchema = Type.Object({ ref: lit(RULE_REFS), times: Type.Optional(Type.Number()) });
export const ClicksForConfidenceSchema = Type.Object({
  fn: Type.Literal("clicks_for_confidence"),
  args: Type.Object({
    p: Type.Union([Type.Number({ exclusiveMinimum: 0, maximum: 1 }), lit(RULE_REFS)]),
    confidence: Type.Number({ exclusiveMinimum: 0, exclusiveMaximum: 1 }),
  }),
});
export const WilsonLowerSchema = Type.Object({
  fn: Type.Literal("wilson_lower"),
  args: Type.Object({
    successes: lit(RULE_METRICS),
    trials: lit(RULE_METRICS),
    z: Type.Optional(Type.Number({ minimum: 0 })),
  }),
});
export const ValueExprSchema = Type.Union([
  Type.Number(),
  RefValueSchema,
  ClicksForConfidenceSchema,
  WilsonLowerSchema,
]);

export const ConditionSchema = Type.Object({
  metric: lit(RULE_METRICS),
  op: lit(COMPARATORS),
  value: ValueExprSchema,
});

/** `all` / `any` groups; one level of nesting is enough for every built-in and the builder. */
const group = <T extends TSchema>(inner: T) =>
  Type.Object({
    all: Type.Optional(Type.Array(inner, { minItems: 1 })),
    any: Type.Optional(Type.Array(inner, { minItems: 1 })),
  });
export const WhenSchema = group(Type.Union([ConditionSchema, group(ConditionSchema)]));

export const RuleDefinitionSchema = Type.Object(
  {
    id: Type.String({ pattern: "^[a-z0-9_]{3,64}$" }),
    name: Type.String({ minLength: 3, maxLength: 120 }),
    description: Type.String({ maxLength: 400 }),
    scope: RuleScopeSchema,
    filters: Type.Optional(
      Type.Object({
        campaign_types: Type.Optional(Type.Array(Type.String())),
        exclude_labels: Type.Optional(Type.Array(Type.String())),
        bidding: Type.Optional(Type.Union([Type.Literal("manual_cpc"), Type.Literal("smart")])),
        enabled_only: Type.Optional(Type.Boolean()),
        /**
         * Search terms (or keywords) whose text contains any of these words or phrases; the rule
         * looks at no other. E.g. ["biscoff"] for "biscoff cookies", "biscoff sugar free".
         */
        term_contains: Type.Optional(
          Type.Array(Type.String({ minLength: 1, maxLength: 80 }), { minItems: 1, maxItems: 200 }),
        ),
        /**
         * search_term scope: the rule looks at words instead of whole search terms, only the words
         * the AI marked irrelevant to their campaign (Search terms › Wording › Ask AI). A word's
         * numbers are those of every search that holds it; add_negative adds the word itself.
         */
        ai_words: Type.Optional(Type.Literal("irrelevant")),
      }),
    ),
    window: Type.Object({
      days: Type.Integer({ minimum: 1, maximum: 365 }),
      /** Omitted: the workspace's conversion lag (guardrail policy). */
      skip_recent_days: Type.Optional(Type.Integer({ minimum: 0, maximum: 30 })),
    }),
    when: WhenSchema,
    then: Type.Object({
      action: RuleActionSchema,
      params: Type.Optional(
        Type.Record(Type.String(), Type.Union([Type.String(), Type.Number(), Type.Boolean()])),
      ),
    }),
    autonomy: AutonomySchema,
    cooldown_days: Type.Integer({ minimum: 0, maximum: 365 }),
    schedule: Type.String({ maxLength: 40 }),
    /** Plain-language Inbox texts with {placeholders} (see core rules/text). */
    text: Type.Optional(
      Type.Object({
        title: Type.Optional(Type.String({ maxLength: 200 })),
        why: Type.Optional(Type.String({ maxLength: 800 })),
      }),
    ),
  },
  { $id: "RuleDefinition" },
);

export type RuleScope = (typeof RULE_SCOPES)[number];
export type RuleAction = (typeof RULE_ACTIONS)[number];
export type AutonomyLevel = (typeof AUTONOMY_LEVELS)[number];
export type RuleMetric = (typeof RULE_METRICS)[number];
export type RuleRef = (typeof RULE_REFS)[number];
export type Comparator = (typeof COMPARATORS)[number];
export type ValueExpr = Static<typeof ValueExprSchema>;
export type Condition = Static<typeof ConditionSchema>;
export interface ConditionGroup {
  all?: Condition[];
  any?: Condition[];
}
export interface When {
  all?: Array<Condition | ConditionGroup>;
  any?: Array<Condition | ConditionGroup>;
}
export type RuleDefinition = Omit<Static<typeof RuleDefinitionSchema>, "when"> & { when: When };

/** Schema errors of a rule document as "path: message" lines; empty when valid. */
export function ruleErrors(def: unknown): string[] {
  return [...Value.Errors(RuleDefinitionSchema, def)].map((e) => `${e.path || "/"}: ${e.message}`);
}
